import UnifiedLightboxModal from '@components/unified-lightbox/Lightbox';
import { openUnifiedLightboxAtom } from '@components/unified-lightbox/atoms';
import { useLiteStorefront } from '@hooks/use-lite-storefront';
import useAnalyticsContext from '@hooks/useAnalyticsContext';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { receiveVendor } from '@redux/vendor';
import { useMedia } from '@tkww/orion-web-app-kernel';
import classNames from 'classnames';
import { useSetAtom } from 'jotai';
import React, { type FC, useEffect, useRef, useState } from 'react';
import type { Gallery, Review } from 'types/reviews';
import AnalyticsConstants from '../../../../../constants/analytics';
import { afterNextPaint } from '../../../../../helpers/after-next-paint';
import Vendor from '../../../../decorators/vendor';
import VendorAnalytics from '../../../../decorators/vendorAnalytics';
import Styles from './Filmstrip.scss';
import { CONSTANTS } from './constants';
import { Thumbnail } from './thumbnail';
import { calculateDisplayCounter } from './utils';

type FilmstripProps = {
	gallery: Gallery[];
	profile?: Vendor.Raw;
	review: Review;
};

const Filmstrip: FC<FilmstripProps> = (props) => {
	const { gallery, profile, review } = props;
	const vendor = useAppSelector(
		(state) => state.vendor.vendor,
	) as Vendor.Decorated;
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [additionalImageCount, setAdditionalImageCount] = useState(0);
	const [viewableImages, setViewableImages] = useState(0);
	const media = useMedia('(min-width: 1024px)');
	const isLiteStorefront = useLiteStorefront();
	const isDesktop = media.yes;

	const [isUnifiedLightboxModalOpen, setIsUnifiedLightboxModalOpen] =
		useState<boolean>(false);
	const setOpenUnifiedLightbox = useSetAtom(openUnifiedLightboxAtom);

	const dispatch = useAppDispatch();
	const isReviewHub = useAppSelector(
		(state) => state.page.pageType === 'review wedding vendors',
	);
	const { track } = useAnalyticsContext();

	useEffect(() => {
		const runDisplayCounter = () => {
			calculateDisplayCounter({
				element: containerRef.current,
				gallery,
				isDesktop: isDesktop,
				setAdditionalImageCount,
				setViewableImages,
			});
		};

		runDisplayCounter();

		window.addEventListener('resize', runDisplayCounter);

		return () => window.removeEventListener('resize', runDisplayCounter);
	}, [isDesktop, gallery]);

	if (gallery.length === 0) {
		return null;
	}

	const handleTrack = () => {
		const selectedVendor = profile ? new Vendor(profile) : vendor;
		const vendorAnalytics = new VendorAnalytics(selectedVendor);
		const sourcePage =
			selectedVendor.purchaseStatus === 'PAID'
				? AnalyticsConstants.PAID_STOREFRONT
				: AnalyticsConstants.UNPAID_STOREFRONT;
		track({
			event: 'Review Interaction',
			properties: {
				...vendorAnalytics.baseEventProps(),
				action: 'view photo',
				portfolioType: AnalyticsConstants.portfolioType.REVIEWS,
				sourcePage,
				sourceContent: 'single review filmstrip',
				mediaCount: gallery.length,
			},
		});
	};

	const onClick = async (index: number) => {
		if (!vendor) {
			return;
		}
		afterNextPaint(() => {
			const shouldPopulateVendor = isReviewHub && !!profile;
			if (shouldPopulateVendor) {
				dispatch(receiveVendor({ data: { storefront: profile } }));
			}

			handleTrack();

			const reviewMediaId = gallery[index].sourceId;
			setOpenUnifiedLightbox({
				indexInAllMedia: index,
				filter: 'single-review',
				reviewMediaId,
				reviewGallery: gallery,
				review,
			});
			setIsUnifiedLightboxModalOpen(true);
		});
	};

	return (
		<>
			<div
				className={classNames(Styles.container, {
					[Styles.nonActionableThumb]: !vendor,
					[Styles.maxImagesShown]:
						!isDesktop && gallery.length >= CONSTANTS.mobileImagesToShow,
				})}
				ref={containerRef}
			>
				{gallery.map(({ sourceId }, idx) => {
					if (idx + 1 < viewableImages) {
						return (
							<>
								<Thumbnail
									sourceId={sourceId}
									index={idx}
									key={sourceId}
									onClick={() => onClick(idx)}
									isDesktop={isDesktop}
								/>
								{isUnifiedLightboxModalOpen && (
									<UnifiedLightboxModal
										entryPoint="Reviews"
										isModalOpen={isUnifiedLightboxModalOpen}
										closeCallback={() => setIsUnifiedLightboxModalOpen(false)}
									/>
								)}
							</>
						);
					}
					if (idx + 1 === viewableImages) {
						return (
							<>
								<Thumbnail
									sourceId={sourceId}
									index={idx}
									key={sourceId}
									onClick={() => onClick(idx)}
									isDesktop={isDesktop}
								>
									<div
										className={classNames(Styles.additionalImagesContainer, {
											[Styles.additionalImagesContainerHidden]:
												!isLiteStorefront && gallery.length <= 3,
										})}
									>
										<span
											className={Styles.additionalImagesText}
										>{`+${additionalImageCount}`}</span>
									</div>
								</Thumbnail>
								{isUnifiedLightboxModalOpen && (
									<UnifiedLightboxModal
										entryPoint="Reviews"
										isModalOpen={isUnifiedLightboxModalOpen}
										closeCallback={() => setIsUnifiedLightboxModalOpen(false)}
									/>
								)}
							</>
						);
					}
					return null;
				})}
			</div>
		</>
	);
};

export default Filmstrip;
